// Dependencies
import axios from 'axios';
import React, { FC, ReactElement, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
// Core & Lab
import AppBar from '@mui/material/AppBar';
import Container from '@mui/material/Container';
import Dialog from '@mui/material/Dialog';
import IconButton from '@mui/material/IconButton';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
// Icons
import Close from '@mui/icons-material/Close';
// Components
import ChannelManagerStockCalendar from './channelManagerStockCalendar';
// Actions
import { setEditStep, setRoomList, updateStockRoomProduct, updateStocksState, updateDateCriteria } from './redux/reducer';
// Types
import { useShowError } from '../../functions/useShowError';
import { AppState } from '../../reducers';
import { Room } from './objects/room';
import { getRoomProductListing } from '../stockStatus/services/getRoomProductListing';
import { setFinished } from '../utils/finished/redux/actions';
import ChannelManagerStockStatusRoomProduct from './channelManagerStockStatusRoomProducts';
import { ChannelManagerStockModal } from './channelManagerStockModal';
import { setLoading } from '../utils/loading/redux/actions';
import { DialogContent } from '@mui/material';
import i18n from '../../translation/i18n';

const headers = {
    'Authorization': `Token ${localStorage.getItem('token')}`,
    'Accept': 'application/json',
    'Content-Type': 'application/json',
    'Accept-Language': i18n.language
};

export const EMPTY_DATE_CRITERIA = { start_date: null, end_date: null };

const ChannelManagerStock: FC = (): ReactElement => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const showError = useShowError();
    const channel_manager_name = useSelector((state: AppState) => state.channelManager?.channel_manager?.name);
    const contract_selected_name = useSelector((state: AppState) => state?.channelManager?.contract_selected?.name);
    const contract_selected_provider_name = useSelector((state: AppState) => state?.channelManager?.contract_selected?.provider?.provider?.name);
    const edit_step = useSelector((state: AppState) => state.channelManager.edit_step);
    const stock_room_products_loading = useSelector((state: AppState) => state.channelManager.stock_room_products_loading);
    const stock_room_product_info = useSelector((state: AppState) => state.channelManager.stock_room_product_info);
    const date_criteria = useSelector((state: AppState) => state.channelManager.date_criteria);
    const client_id = useSelector((state: AppState) => state.header.client_id);
    const channel_manager = useSelector((state: AppState) => state.channelManager.channel_manager);
    const room_list = useSelector((state: AppState) => state.channelManager.room_list);
    const [date_criteria_open, setDateCriteriaOpen] = useState<boolean>(true);


    const setDateCriteria = (date_criteria: { start_date: Date | null, end_date: Date | null }) => {
        dispatch(updateDateCriteria(date_criteria));
        setDateCriteriaOpen(false);
    };

    const onToggleStockDisplay = () => {
        dispatch(setEditStep('INFO'));
        setDateCriteria(EMPTY_DATE_CRITERIA);
    };

    const getMissingPrice = async (room: Room): Promise<Room> => {
        try {
            const response = await axios({
                method: 'GET',
                url: `${API_HREF}channel_manager/accommodation_rooms/`,
                headers,
                params: {
                    source: channel_manager?.source,
                    accom_id: channel_manager?.id,
                    client_hotel_identifier: channel_manager?.client_hotel_identifier,
                    room_id: room.id
                }
            });
            const data: Room = Array.isArray(response.data) ? response.data[0] : response.data;
            return data;
        } catch (error) {
            console.log('error : ', error);
            return room;
        }
    };

    useEffect(() => {
        if (date_criteria.start_date && date_criteria.end_date) {
            dispatch(setLoading(true));
            const rooms_missing_prices = room_list.filter(room => room.prices === null);
            if (rooms_missing_prices.length > 0 && channel_manager) {
                Promise.all(rooms_missing_prices.map(room => getMissingPrice(room)))
                    .then((updated_rooms) => {
                        const new_room_list = room_list.map(room => {
                            const updated = updated_rooms.find(r => r.id === room.id);
                            return updated ?? room;
                        });
                        dispatch(setRoomList(new_room_list));
                        setTimeout(() => {
                            dispatch(updateStocksState(date_criteria as { start_date: Date; end_date: Date; }));
                            dispatch(setLoading(false));
                        }, 0);
                    });
            } else {
                setTimeout(() => {
                    dispatch(updateStocksState(date_criteria as { start_date: Date; end_date: Date; }));
                    dispatch(setLoading(false));
                }, 0);
            }
        }
    }, [date_criteria]);

    useEffect(() => {
        if (stock_room_products_loading && client_id && stock_room_product_info) {
            dispatch(setFinished(true));
            getRoomProductListing({ real_stock_id: stock_room_product_info.real_stock.id }, client_id)
                .then((response) => {
                    dispatch(updateStockRoomProduct(response.data));
                    dispatch(setFinished(false));
                }).catch((error) => {
                    dispatch(updateStockRoomProduct(null));
                    dispatch(setFinished(false));
                    showError(error);
                });
        }
    }, [stock_room_products_loading]);

    useEffect(() => {
        setDateCriteriaOpen(true);
        dispatch(updateDateCriteria({ start_date: null, end_date: null }));
    }, [edit_step]);

    return (
        <>
            <ChannelManagerStockStatusRoomProduct />
            <Dialog fullScreen open={edit_step === 'STOCKS_STATE'} onClose={onToggleStockDisplay}>
                <AppBar className={'ft-user-appBar'}>
                    <Toolbar>
                        <IconButton edge={'start'} color={'inherit'} onClick={onToggleStockDisplay}><Close /></IconButton>
                        <Typography variant={'h6'}>{t('shared.stock')} :  {channel_manager_name} - {contract_selected_provider_name} - {contract_selected_name}</Typography>
                    </Toolbar>
                </AppBar>
                <DialogContent sx={{ padding: '0 30px' }}>
                    <ChannelManagerStockCalendar setDateCriteriaOpen={setDateCriteriaOpen}/>
                </DialogContent>
            </Dialog>

            <ChannelManagerStockModal open={date_criteria_open} onClose={() => setDateCriteriaOpen(false)} onSaveForm={(dates) => setDateCriteria(dates)} />
        </>
    );
};

export default ChannelManagerStock;
